import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { FormEvent, ReactNode } from "react";
import { useId } from "react";

export interface FormModalProps {
    open: boolean;
    title: ReactNode;
    children?: ReactNode; // form fields
    onSubmit?: (event: FormEvent) => void;
    onCancel: () => void;
    submitText?: string;
    cancelText?: string;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
    fullWidth?: boolean;
    formId?: string; // allow external form linkage
    confirmDisabled?: boolean;
    confirmLoading?: boolean;
    keepMounted?: boolean;
}

export function FormModal({
    open,
    title,
    children,
    onSubmit,
    onCancel,
    submitText = "Save",
    cancelText = "Cancel",
    maxWidth = "sm",
    fullWidth = true,
    formId,
    confirmDisabled,
    confirmLoading = false,
    keepMounted,
}: FormModalProps) {
    const titleId = useId();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Wrap content in an optional form if onSubmit provided and no external formId
    const usesInternalForm = !!onSubmit && !formId;

    // If we're using an internal form, generate an id so the submit button (which
    // lives outside the form in DialogActions) can reference it via the `form`
    // attribute. If an external `formId` was provided, use that instead.
    const internalFormId =
        formId ?? (usesInternalForm ? `${titleId}-form` : undefined);

    // Handle form submission - always prevent default to avoid page reload
    function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (onSubmit) {
            onSubmit(event);
        }
    };

    const content = <DialogContent>{children}</DialogContent>;
    const confirmButtonDisabled = confirmDisabled || confirmLoading;

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby={titleId}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={isMobile}
            keepMounted={keepMounted}
        >
            <DialogTitle id={titleId}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Typography component="span" variant="h6" fontWeight={700}>
                        {title}
                    </Typography>
                </Stack>
            </DialogTitle>
            <Divider />
            {usesInternalForm ? (
                <Box
                    component="form"
                    id={internalFormId}
                    onSubmit={handleFormSubmit}
                    noValidate
                >
                    {content}
                </Box>
            ) : (
                content
            )}
            <Divider />
            <DialogActions sx={{ px: 3, py: 2.25 }}>
                <Button onClick={onCancel} variant="text" color="inherit">
                    {cancelText}
                </Button>
                <Button
                    type={internalFormId ? "submit" : "button"}
                    form={internalFormId}
                    onClick={!internalFormId ? onSubmit : undefined}
                    variant="contained"
                    color="primary"
                    disabled={confirmButtonDisabled}
                    startIcon={
                        confirmLoading ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : undefined
                    }
                    aria-busy={confirmLoading ? "true" : undefined}
                >
                    {submitText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default FormModal;
